import { PatientTimeslotDayOfWeek } from 'gql-types';
import moment, { Moment } from 'moment-timezone';
import { nanoid } from 'nanoid';

import { formatTimeRange } from './formatting';

export enum DayOfWeek {
    SUNDAY = 'Sun',
    MONDAY = 'Mon',
    TUESDAY = 'Tue',
    WEDNESDAY = 'Wed',
    THURSDAY = 'Thu',
    FRIDAY = 'Fri',
    SATURDAY = 'Sat',
}

export type WeekdayTimeWindow = {
    startTime: string;
    endTime: string;
    day: PatientTimeslotDayOfWeek;
};

export type WeekdayTimeWindowCalendarEvent = {
    id: string;
    start: Date;
    end: Date;
    title: string;
};

export type DayStartEnd = {
    day: PatientTimeslotDayOfWeek;
    start: Moment;
    end: Moment;
};

export const daysOfWeek = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
];

export const renderFullWeekdayName = (dayOfWeek: DayOfWeek) => {
    return daysOfWeek[Object.values(DayOfWeek).indexOf(dayOfWeek)];
};

export const renderFullWeekdayNameFromAbbr = (abbr: PatientTimeslotDayOfWeek) => {
    return daysOfWeek[
        [
            PatientTimeslotDayOfWeek.SUN,
            PatientTimeslotDayOfWeek.MON,
            PatientTimeslotDayOfWeek.TUE,
            PatientTimeslotDayOfWeek.WED,
            PatientTimeslotDayOfWeek.THU,
            PatientTimeslotDayOfWeek.FRI,
            PatientTimeslotDayOfWeek.SAT,
        ].indexOf(abbr)
    ];
};

export const dayOfWeekAbbrToIndex = {
    SUN: 0,
    MON: 1,
    TUE: 2,
    WED: 3,
    THU: 4,
    FRI: 5,
    SAT: 6,
};

export const indexToDayOfWeekAbbr = [
    PatientTimeslotDayOfWeek.SUN,
    PatientTimeslotDayOfWeek.MON,
    PatientTimeslotDayOfWeek.TUE,
    PatientTimeslotDayOfWeek.WED,
    PatientTimeslotDayOfWeek.THU,
    PatientTimeslotDayOfWeek.FRI,
    PatientTimeslotDayOfWeek.SAT,
];

export const timeStringtoHoursMinutes = (timeString: string) => {
    const [hours, minutes] = timeString.split(':').map((timePart) => parseInt(timePart));
    return { hours, minutes };
};

export const weekdayTimeWindowsToWeekdayTimeWindowCalendarEvents = (
    weekdayTimeWindows: WeekdayTimeWindow[],
): WeekdayTimeWindowCalendarEvent[] => {
    return weekdayTimeWindows.map((timeWindow) => {
        const dayOfWeek = dayOfWeekAbbrToIndex[timeWindow.day];
        const { hours: startHours, minutes: startMinutes } = timeStringtoHoursMinutes(
            timeWindow.startTime,
        );
        const { hours: endHours, minutes: endMinutes } = timeStringtoHoursMinutes(
            timeWindow.endTime,
        );
        // Conveniently, new Date(0, 0, 0, 0, 0) is a Sunday so we can directly plug in the day index
        const start = new Date(0, 0, dayOfWeek, startHours, startMinutes);
        const end = new Date(0, 0, dayOfWeek, endHours, endMinutes);
        return {
            // "But wait!" you cry out, "Why are we assigning a random ID here?"
            // Good question! The answer is: the IDs don't actually matter because all timeslot preferences are deleted and recreated
            // in the backend when they are updated and we don't need to pass IDs to the backend at all. Furthermore, we need to be
            // able to add new timeslot preferences in the frontend and we need the IDs attached to them to not collide with any others. So basically,
            // the IDs don't need to be meaningful, they just need to be unique. Thus we can just assign a random UUID here and for new timeslots and
            // it will be overwritten by the backend anyway.
            id: nanoid(),
            start,
            end,
            title: formatTimeRange(moment(start), moment(end), null, { showTimezone: false }),
        };
    });
};

export const dayStartEndToWeekdayTimeWindow = (
    dayStartEnd: DayStartEnd,
): WeekdayTimeWindowCalendarEvent => {
    // Convert the moments + day into start and end dates
    const dayIndex = dayOfWeekAbbrToIndex[dayStartEnd.day];
    const startHour = dayStartEnd.start.hour();
    const startMinute = dayStartEnd.start.minute();
    const endHour = dayStartEnd.end.hour();
    const endMinute = dayStartEnd.end.minute();
    // Conveniently, new Date(0, 0, 0, 0, 0) is a Sunday so we can directly plug in the day index
    const newStart = new Date(0, 0, dayIndex, startHour, startMinute);
    const newEnd = new Date(0, 0, dayIndex, endHour, endMinute);
    return {
        start: newStart,
        end: newEnd,
        id: nanoid(),
        title: formatTimeRange(moment(newStart), moment(newEnd), null, { showTimezone: false }),
    };
};

export const doesNewTimeslotOverlapExistingTimeslots = (
    newTimeslot: DayStartEnd,
    existingTimeslots: WeekdayTimeWindowCalendarEvent[],
): boolean => {
    const newStart = moment(newTimeslot.start);
    const newEnd = moment(newTimeslot.end);

    return existingTimeslots.some((timeslot) => {
        const existingStart = moment(timeslot.start);
        const existingEnd = moment(timeslot.end);

        return newStart.isSameOrBefore(existingEnd) && newEnd.isSameOrAfter(existingStart);
    });
};
